import { Euler, Object3D, Quaternion, Vector3 } from "three";
import { Engine } from "../../engine/Engine";
import { CSS3DObject } from "../../engine/renderer/CSS3DRenderer";

const RIGHT = new Vector3(1, 0, 0);

export class CSS3DObjectElement extends Object3D {
  protected _object: CSS3DObject;
  protected _scaleObject: Object3D;
  protected _rotationObect: Object3D;

  private _cameraStartInstance;
  private _cameraEndInstance;
  private _cameraChangeInstance;

  protected _options: {
    minScale?: number;
    maxScale?: number;
    rotation?: Euler;
    offset?: Vector3;
    debug?: boolean;
  };

  constructor(
    element: HTMLDivElement,
    name: string,
    options: {
      minScale?: number;
      maxScale?: number;
      rotation?: Euler;
      offset?: Vector3;
      debug?: boolean;
    } = {
      minScale: -1,
      maxScale: -1,
      rotation: new Euler(Math.PI / 2, 0, 0),
      offset: new Vector3(),
      debug: false,
    }
  ) {
    super();

    this._object = new CSS3DObject(element);
    this._scaleObject = new Object3D();
    this._scaleObject.add(this._object);
    this._rotationObect = new Object3D();
    this._rotationObect.add(this._scaleObject);
    this._rotationObect.rotation.copy(options.rotation!);

    this.add(this._rotationObect);
    this.name = name;
    this._object.name = name;

    this._object.scale.set(1 / 100, 1 / 100, 1 / 100);
    this._options = options;
    this._updateScale(Engine.activeEngine.activeCamera?.instance);

    this._cameraStartInstance = this._cameraStart.bind(this);
    Engine.activeEngine.addEventListener(
      "camera-start",
      this._cameraStartInstance
    );

    this._cameraChangeInstance = this._cameraChange.bind(this);
    Engine.activeEngine.addEventListener(
      "camera-change",
      this._cameraChangeInstance
    );

    this._cameraEndInstance = this._cameraEnd.bind(this);
    Engine.activeEngine.addEventListener("camera-end", this._cameraEndInstance);

    this.addEventListener("added", () => {
      this._scaleObject.add(this._object);
    });

    this.addEventListener("removed", () => {
      this._scaleObject.remove(this._object);
    });
  }

  private _tmpCheckRight = new Vector3();
  private _tmpCheckQuaternion = new Quaternion();

  private _tmpCameraRight = new Vector3();
  private _tmpCameraQuaternion = new Quaternion();

  private _cameraChange(e: any) {
    //this.visible = false;
    //this._updateScale(e.detail);
  }

  private _cameraEnd(e: any) {
    //this.visible = true;
    this._updateScale(e.detail);
  }

  private _cameraStart(e: any) {
    //this.visible = false;
  }

  private _getScaleMagnitude() {
    this.updateWorldMatrix(true, true);
    this.getWorldQuaternion(this._tmpCheckQuaternion);
    this._tmpCheckRight.copy(RIGHT);
    this._tmpCheckRight = this._tmpCheckRight.applyQuaternion(
      this._tmpCheckQuaternion
    );

    Engine.activeEngine.activeCamera?.instance.updateWorldMatrix(true, true);
    Engine.activeEngine.activeCamera?.instance.getWorldQuaternion(
      this._tmpCameraQuaternion
    );
    this._tmpCameraRight.copy(RIGHT);
    this._tmpCameraRight.applyQuaternion(this._tmpCameraQuaternion);

    return this.round(this._tmpCameraRight.dot(this._tmpCheckRight)) > 0.0
      ? -1
      : 1;
  }

  round(value: number) {
    if (Math.abs(value) < 0.0001) {
      return 0;
    }
    return value;
  }

  public update() {
    this._updateScale(Engine.activeEngine.activeCamera?.instance);
  }

  private _updateScale(camera: any) {
    let options = this._options;

    let direction = this._getScaleMagnitude();
    let scale = 1;

    if (camera.type == "OrthographicCamera") {
      let zoom = camera.userData.controller.maxZoom;
      scale = (0.5 * zoom) / camera.zoom;
    } else {
      let zoom = camera.position.distanceTo(camera.userData.controller.target);
      scale = 0.25 * zoom;
    }

    if (options.minScale && options.minScale > -1 && scale < options.minScale) {
      scale = options.minScale;
    }

    if (options.maxScale && options.maxScale > -1 && scale > options.maxScale) {
      scale = options.maxScale;
    }

    this._object!.scale.set(
      (direction * -scale) / 100,
      scale / 100,
      scale / 100
    );

    if (this._options.rotation)
      this._rotationObect.rotation.set(
        direction * this._options.rotation.x,
        0, //Math.PI / 2,
        this._options.rotation.z
      );

    if (this._options.offset) {
      this._rotationObect.position.copy(
        this._options.offset.clone().multiplyScalar(scale)
      );
    }
  }

  removeFromParent(): this {
    this._scaleObject.remove(this._object);

    return super.removeFromParent();
  }

  dispose() {
    this._scaleObject.remove(this._object);
    this._object.element.remove();
    Engine.activeEngine.removeEventListener(
      "camera-start",
      this._cameraStartInstance
    );
    Engine.activeEngine.removeEventListener(
      "camera-end",
      this._cameraEndInstance
    );
    Engine.activeEngine.removeEventListener(
      "camera-change",
      this._cameraChangeInstance
    );
  }
}
